"use client";

import { useCallback, useRef, useState } from "react";

const DRAG_THRESHOLD = 6;
const EDGE_MARGIN = 12;

export type EdgeSide = "left" | "right";
export type EdgePosition = { side: EdgeSide; top: number };

/**
 * Cho phép kéo một khối (gắn qua containerRef) di chuyển tự do theo trục dọc,
 * khi thả ra sẽ tự "dính" (snap) vào mép trái hoặc mép phải màn hình tuỳ theo
 * vị trí thả gần bên nào hơn. Vị trí chỉ tồn tại trong phiên hiện tại (state
 * trong bộ nhớ) — không lưu localStorage, nên refresh trang sẽ quay lại vị
 * trí mặc định ban đầu (đặt bằng CSS: mép phải).
 *
 * Cách dùng: gắn `dragHandlers` vào phần tử đóng vai trò "tay cầm" kéo
 * (ví dụ nút chat chính), còn `containerRef` trỏ tới khối cha sẽ di chuyển.
 */
export function useEdgeDraggable(
  containerRef: React.RefObject<HTMLElement | null>
) {
  const [position, setPosition] = useState<EdgePosition | null>(null);
  // true trong lúc đang thực sự kéo (đã vượt ngưỡng DRAG_THRESHOLD) — dùng để
  // tắt tooltip trong lúc kéo, ví dụ: <Tooltip open={isDragging ? false : undefined}>
  const [isDragging, setIsDragging] = useState(false);
  // true trong khoảng thời gian ngắn ngay sau khi vừa kéo xong, để onClick
  // của tay cầm biết mà bỏ qua (tránh kéo xong lại bị coi là click mở popup)
  const wasDraggedRef = useRef(false);
  const dragStateRef = useRef<{
    startX: number;
    startY: number;
    startTop: number;
    dragging: boolean;
  } | null>(null);

  const clampTop = useCallback(
    (top: number) => {
      const el = containerRef.current;
      const height = el?.offsetHeight ?? 0;
      const maxTop = window.innerHeight - height - EDGE_MARGIN;
      return Math.min(
        Math.max(top, EDGE_MARGIN),
        Math.max(maxTop, EDGE_MARGIN)
      );
    },
    [containerRef]
  );

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      dragStateRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startTop: rect.top,
        dragging: false,
      };
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    },
    [containerRef]
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const state = dragStateRef.current;
      const el = containerRef.current;
      if (!state || !el) return;

      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;

      if (!state.dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
          return;
        }
        state.dragging = true;
        wasDraggedRef.current = true;
        setIsDragging(true);
      }

      const newTop = clampTop(state.startTop + dy);
      el.style.top = `${newTop}px`;
      el.style.bottom = "auto";
    },
    [clampTop, containerRef]
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const state = dragStateRef.current;
      const el = containerRef.current;
      dragStateRef.current = null;
      setIsDragging(false);
      if (!state || !el) return;

      (e.target as HTMLElement).releasePointerCapture?.(e.pointerId);

      if (!state.dragging) return;

      const rect = el.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const side: EdgeSide = centerX < window.innerWidth / 2 ? "left" : "right";
      const top = clampTop(rect.top);

      const next: EdgePosition = { side, top };
      setPosition(next);
    },
    [clampTop, containerRef]
  );

  return {
    position,
    isDragging,
    wasDraggedRef,
    dragHandlers: { onPointerDown, onPointerMove, onPointerUp },
  };
}